import { For, Show, createMemo } from "solid-js";
import type { Component } from "solid-js";
import { usePaymentListQuery } from "../service";
import { useShellStore } from "@ui/state/shell";
import { TableDate } from "@ui/components/TableDate";

const resolve = <T,>(input: T | (() => T)): T => (typeof input === "function" ? (input as () => T)() : input);

const formatCurrency = (amount: number | null, currency: string | null): string => {
  if (amount === null || Number.isNaN(amount)) {
    return "-";
  }
  if (!currency) {
    return amount.toLocaleString();
  }
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(amount);
  } catch (_error) {
    return `${currency} ${amount}`;
  }
};

// formatDateTime removed - using TableDate component instead

export const PaymentListView: Component = () => {
  const query = usePaymentListQuery();
  const {
    state,
    actions: { selectPayment },
  } = useShellStore();

  const payments = createMemo(() => resolve(query.data) ?? []);
  const isLoading = () => resolve(query.isLoading);
  const isFetching = () => resolve(query.isFetching);

  return (
    <section class="chat-list-view">
      <header class="chat-list-header">
        <div>
          <h1 class="main-placeholder-title">Payment ledger</h1>
          <p>Monitor Stripe intents and ensure each booking has a confirmed payment.</p>
        </div>
        <div class="chat-list-actions">
          <button
            type="button"
            class="button-primary refresh-button"
            onClick={() => void query.refetch()}
            disabled={isFetching()}
            title="Refresh"
          >
            <i class={`fa-solid fa-rotate-right ${isFetching() ? "spinning" : ""}`}></i>
          </button>
        </div>
      </header>

      <Show when={!isLoading()} fallback={<div class="placeholder-card">Loading payments...</div>}>
        <Show when={payments().length > 0} fallback={<div class="placeholder-card">No payments captured yet.</div>}>
          <table class="chat-table">
            <thead>
              <tr>
                <th>Payment</th>
                <th>Booking</th>
                <th>Customer</th>
                <th>Status</th>
                <th>Amount</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              <For each={payments()}>
                {(payment) => (
                  <tr
                    class={state.selectedPaymentId === payment.id ? "is-selected" : ""}
                    onClick={() => selectPayment(payment.id, payment.userId)}
                  >
                    <td class="chat-user">{payment.paymentIntentId ?? "Pending"}</td>
                    <td class="chat-preview">{payment.bookingId}</td>
                    <td>{payment.userId ?? "Guest"}</td>
                    <td>
                      <span class={`booking-status booking-status--${payment.status}`}>{payment.status}</span>
                    </td>
                    <td>{formatCurrency(payment.amount, payment.currency)}</td>
                    <td class="chat-time"><TableDate date={payment.updatedAt ?? payment.createdAt} /></td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </Show>
      </Show>
    </section>
  );
};
